import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { graphql } from '../gql';
import { IQueryGetEntitiesFieldArgs } from './types';
import {
  getFirstPaginatedNode,
  getPaginatedNodes,
  getPaginationTotalResults,
  toPagination,
} from './utils';

export const GET_ENTITIES_VULNERABILITIES = graphql(`
  query getVulnerabilities($pagination: PaginationRequest, $uuid: String) {
    entities(filters: { submissionType: INSURED, submissionUuid: $uuid }) {
      edges {
        cursor
        node {
          softwares(pagination: $pagination) {
            pageInfo {
              totalResults
              pageNumber
              pageSize
              hasNextPage
              hasPreviousPage
            }
            edges {
              cursor
              node {
                uuid
                edition
                language
                part
                product
                type
                update
                version
                createdAt
                updatedAt
                vulnerabilities {
                  pageInfo {
                    totalResults
                  }
                  edges {
                    cursor
                    node {
                      uuid
                      cve
                      type
                      publishDate
                      updateDate
                      score
                      access
                      complexity
                      authentication
                      conf
                      integ
                      createdAt
                      updatedAt
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export function useGetEntitiesVulnerabilitiesQuery({
  submissionUuid,
  pageNumber,
  pageSize,
}: IQueryGetEntitiesFieldArgs) {
  const { loading, error, data } = useQuery(GET_ENTITIES_VULNERABILITIES, {
    variables: {
      uuid: submissionUuid,
      pagination: toPagination(pageNumber, pageSize),
    },
  });

  const { softwares, totalSoftware, totalVulnerabilities } = useMemo(() => {
    if (loading) {
      return {
        softwares: [],
        totalSoftware: 0,
        totalVulnerabilities: 0,
      };
    }
    const entity = getFirstPaginatedNode(data?.entities);
    return {
      softwares: getPaginatedNodes(entity?.softwares),
      totalSoftware: getPaginationTotalResults(entity?.softwares),
      totalVulnerabilities: entity?.softwares.edges?.reduce(
        (acc, software) =>
          acc +
          (getPaginationTotalResults(software.node?.vulnerabilities) ?? 0),
        0
      ),
    };
  }, [data, loading]);

  return {
    loading,
    error,
    data,
    softwares,
    totalSoftware,
    totalVulnerabilities,
  };
}
